import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {ApiToken, SubClient, TokenStatus} from '../../../../../services/cellcast.v2.types'
import {
  useLazyGetSubClientTokensQuery,
  useUpdateSubClientTokenStatusMutation,
} from '../../../../../services/cellcast.v2'
import {OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import WSButton from '../../../../../_metronic/helpers/components/WSButton/WSButton'
import {toast} from 'react-toastify'
import moment from 'moment'
import {StatusColor} from '../../../../Utils/Constant/ArrayOfSideNav'
import {KTSVG} from '../../../../../_metronic/helpers'
import DeleteToken from '../Modal/DeleteToken'

export type ApiTableProps = {
  reload: boolean
  setReload: (reload: boolean) => void
  data: SubClient | undefined
}

const ApiTable: FC<ApiTableProps> = (props) => {
  const {data: user, reload, setReload} = props
  const [fetchToken, {isLoading, isFetching}] = useLazyGetSubClientTokensQuery()
  const [updateStatus, {isLoading: updateStatusLoading}] = useUpdateSubClientTokenStatusMutation()
  const [updateStatusId, setUpdateStatusId] = useState('')
  const [tokens, setTokens] = useState<ApiToken[]>([])

  const [deleteId, setDeleteId] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  useEffect(() => {
    if (user && user._id && reload === true) {
      fetchToken(user._id)
        .then((res) => {
          if ('data' in res) {
            setTokens(res.data ? res.data.data : [])
          } else if ('error' in res) {
            const errObj = res.error as any
            if (errObj.data.message) {
              toast.error(errObj.data.message)
            } else {
              toast.error(res.error)
            }
          }
        })
        .catch((err) => {
          toast.error(err)
        })
        .finally(() => {
          setReload(false)
        })
    }
  }, [user, reload])

  const handleUpdateStatus = useCallback(
    (tokenId: string, status: TokenStatus) => {
      if (!user) {
        return
      }
      setUpdateStatusId(tokenId)

      updateStatus({
        id: user._id,
        data: {tokenId, status},
      })
        .then((res) => {
          if ('data' in res) {
            toast.success(res.data?.message)
            const index = tokens.findIndex((el) => el._id === tokenId)
            if (index > -1) {
              const tmpData = [...tokens]
              tmpData[index] = {...tmpData[index], status: status}
              setTokens([...tmpData])
            }
          } else if ('error' in res) {
            toast.error(res.error)
          }
        })
        .catch((err) => {
          toast.error(err.error)
        })
        .finally(() => {
          setUpdateStatusId('')
        })
    },
    [user, updateStatus, tokens]
  )

  const DeleteTokenModal = useMemo(
    () => (
      <DeleteToken
        show={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false)
          setDeleteId('')
        }}
        userId={user?._id || ''}
        deleteId={deleteId}
        callbackFn={() => {
          const tmpData = tokens.filter((el) => el._id !== deleteId)
          setTokens([...tmpData])

          setShowDeleteModal(false)
          setDeleteId('')
        }}
        message='Are you sure, you want to delete this api token ?'
        description='this will delete data permanently'
      />
    ),
    [deleteId, showDeleteModal, user?._id]
  )

  return (
    <div className='card'>
      {DeleteTokenModal}
      <div className='card-header'>
        <div className='card-title'>
          <h3 className='fs-2'>API Keys</h3>
        </div>
      </div>
      <div className='card-body p-0'>
        {isLoading || isFetching ? (
          <div className='row flex-center h-200 mb-2'>
            <Spinner />
          </div>
        ) : (
          <div className='table-responsive'>
            <table
              className='table table-flush align-middle table-row-bordered table-row-solid gy-4 gs-9'
              id='kt_api_keys_table'
            >
              <thead className='fs-5 text-gray-500'>
                <tr>
                  <th className='w-200px min-w-150px ps-9'>Label</th>
                  <th className='w-200px min-w-150 px-0'>API Keys</th>
                  <th className='w-30px min-w-20px'></th>
                  <th className='min-w-125px min-w-125px'>Created</th>
                  <th className='min-w-125px'>Status</th>
                  <th className='w-100px'></th>
                </tr>
              </thead>
              <tbody className='fs-6 fw-bold text-gray-600'>
                {tokens.map((token, i) => (
                  <tr key={i}>
                    <td className='ps-9'>{token.name}</td>
                    <td data-bs-target='license' className='ps-0'>
                      <span className='d-inline-block text-truncate' style={{maxWidth: '250px'}}>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip id='button-tooltip'>{token.token}</Tooltip>}
                        >
                          <div className='text-truncate'>{token.token}</div>
                        </OverlayTrigger>
                      </span>
                    </td>
                    <td>
                      <WSButton
                        onClick={() => {
                          navigator.clipboard.writeText(token.token)
                          toast.success('Copied!')
                        }}
                        data-action='copy'
                        className='btn btn-active-color-primary btn-icon btn-sm btn-outline-light'
                      >
                        <span className='svg-icon svg-icon-2'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='24'
                            height='24'
                            viewBox='0 0 24 24'
                            fill='none'
                          >
                            <path
                              opacity='0.5'
                              d='M18 2H9C7.34315 2 6 3.34315 6 5H8C8 4.44772 8.44772 4 9 4H18C18.5523 4 19 4.44772 19 5V16C19 16.5523 18.5523 17 18 17V19C19.6569 19 21 17.6569 21 16V5C21 3.34315 19.6569 2 18 2Z'
                              fill='currentColor'
                            />
                            <path
                              fill-rule='evenodd'
                              clipRule='evenodd'
                              d='M14.7857 7.125H6.21429C5.62255 7.125 5.14286 7.6007 5.14286 8.1875V18.8125C5.14286 19.3993 5.62255 19.875 6.21429 19.875H14.7857C15.3774 19.875 15.8571 19.3993 15.8571 18.8125V8.1875C15.8571 7.6007 15.3774 7.125 14.7857 7.125ZM6.21429 5C4.43908 5 3 6.42709 3 8.1875V18.8125C3 20.5729 4.43909 22 6.21429 22H14.7857C16.5609 22 18 20.5729 18 18.8125V8.1875C18 6.42709 16.5609 5 14.7857 5H6.21429Z'
                              fill='currentColor'
                            />
                          </svg>
                        </span>
                      </WSButton>
                    </td>
                    <td>{moment(token.createdAt)?.format('DD/MM/YYYY')}</td>
                    <td>
                      <span
                        className={`badge badge-light-${StatusColor?.[token.status]} fs-7 fw-bold`}
                      >
                        {token.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className='pe-9'>
                      <div className='d-flex flex-row align-items-center'>
                        {updateStatusLoading && updateStatusId === token._id ? (
                          <label className='col-6 text-center'>...</label>
                        ) : (
                          <label className='col-6 switch me-4'>
                            <input
                              checked={token.status === TokenStatus.Active}
                              onChange={() =>
                                handleUpdateStatus(
                                  token._id,
                                  token.status === TokenStatus.Active
                                    ? TokenStatus.Inactive
                                    : TokenStatus.Active
                                )
                              }
                              className='form-check-input h-20px w-30px'
                              type='checkbox'
                              value=''
                              id='flexSwitchDefault'
                            />
                            <span className='slider round'></span>
                          </label>
                        )}

                        <a
                          onClick={() => {
                            setDeleteId(token._id)
                            setShowDeleteModal(true)
                          }}
                          className='col-6 btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1 trash'
                        >
                          <span className='svg-icon svg-icon-3'>
                            <KTSVG
                              path='/media/icons/duotune/art/trush.svg'
                              className='svg-icon-3'
                            />
                          </span>
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default ApiTable
